<script setup lang="ts">
/**
 * Правка страницы базы знаний — макет 23:53: полноширинный редактор слева,
 * панель версий 320 справа, статус черновика и «Сохранить версию» в шапке.
 *
 * Правки уходят на сервер сами, через паузу после последнего нажатия: текст
 * страницы пишут долго, и «забыл сохранить» здесь стоит дороже лишнего
 * запроса. Версию из автосейва сервис не делает — снимок появляется только по
 * кнопке, иначе история за день превращается в ленту нажатий.
 *
 * Поэтому «Отменить» возвращает страницу к тому, что было при входе в правку:
 * черновик уже на сервере, и без этого отката кнопка не отменяла бы ничего.
 */
import { AppButton, AppModal, AppTextField, useToast } from '@vinprop/design-system'
import { AppRichTextEditor } from '@vinprop/design-system/editor'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { errorMessage } from '@/api/errors'
import TrackerToolbar from '@/components/TrackerToolbar.vue'
import { uploadEditorImage } from '@/features/editor'
import { pathTo, type PageVersion } from '@/features/knowledge/api'
import KbVersions from '@/features/knowledge/components/KbVersions.vue'
import { useKnowledgeStore } from '@/features/knowledge/store'

/** Пауза после последнего нажатия, через которую черновик уходит на сервер */
const AUTOSAVE_DELAY = 1500

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const store = useKnowledgeStore()
const toast = useToast()

const pageId = computed(() => String(route.params.pageId ?? ''))

const title = ref('')
const contentMd = ref('')
const savedAt = ref('')
const isSaving = ref(false)
const askVersion = ref(false)
const comment = ref('')
const preview = ref<PageVersion | null>(null)

/** Состояние на момент входа — к нему возвращает «Отменить». */
const initial = ref({ title: '', contentMd: '' })

let timer: ReturnType<typeof setTimeout> | undefined

const crumbs = computed(() => {
  const path = store.page ? pathTo(store.pages, store.page.id) : []

  return [
    { label: t('nav.knowledge'), to: '/kb' },
    ...path.slice(0, -1).map((entry) => ({ label: entry.title, to: `/kb/${entry.id}` })),
    { label: t('kb.edit.crumb', { title: store.page?.title ?? '' }) },
  ]
})

const stamp = () =>
  new Intl.DateTimeFormat(locale.value === 'en' ? 'en-GB' : 'ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date())

const flush = async () => {
  if (!store.page) return

  isSaving.value = true

  try {
    await store.savePage(title.value, contentMd.value)
    savedAt.value = stamp()
  } catch (failure) {
    toast.error(errorMessage(failure, t('kb.edit.saveFailed')))
  } finally {
    isSaving.value = false
  }
}

const schedule = () => {
  if (timer) clearTimeout(timer)

  timer = setTimeout(() => void flush(), AUTOSAVE_DELAY)
}

const load = async () => {
  await store.ensureSpaces()
  await store.openPage(pageId.value)
  await store.loadVersions(pageId.value)

  title.value = store.page?.title ?? ''
  contentMd.value = store.page?.contentMd ?? ''
  initial.value = { title: title.value, contentMd: contentMd.value }
}

const saveVersion = async () => {
  // Черновик мог не успеть уйти: версия снимается с того, что лежит в базе.
  if (timer) clearTimeout(timer)

  await flush()

  try {
    await store.commitVersion(comment.value.trim())
    askVersion.value = false
    comment.value = ''
    toast.success(t('kb.edit.versionSaved'))
  } catch (failure) {
    toast.error(errorMessage(failure, t('kb.edit.saveFailed')))
  }
}

const cancel = async () => {
  if (timer) clearTimeout(timer)

  title.value = initial.value.title
  contentMd.value = initial.value.contentMd

  await flush()
  await router.push(`/kb/${pageId.value}`)
}

const restore = async (version: PageVersion) => {
  if (timer) clearTimeout(timer)

  try {
    await store.restore(version.number)
    title.value = store.page?.title ?? ''
    contentMd.value = store.page?.contentMd ?? ''
    preview.value = null
    toast.success(t('kb.edit.restored', { number: version.number }))
  } catch (failure) {
    toast.error(errorMessage(failure, t('kb.edit.saveFailed')))
  }
}

watch([title, contentMd], ([nextTitle, nextContent]) => {
  if (!store.page) return
  if (nextTitle === store.page.title && nextContent === store.page.contentMd) return

  schedule()
})

watch(pageId, () => void load())

onMounted(load)

// Уход с экрана не должен съедать последние набранные буквы.
onBeforeUnmount(() => {
  if (!timer) return

  clearTimeout(timer)
  void flush()
})
</script>

<template>
  <TrackerToolbar :breadcrumbs="crumbs">
    <p v-if="preview" class="kbedit__status kbedit__status--preview">
      {{ t('kb.edit.previewing', { number: preview.number }) }}
    </p>
    <p v-else-if="savedAt" class="kbedit__status">
      {{ t('kb.edit.savedAt', { time: savedAt }) }}
    </p>

    <AppButton v-if="preview" variant="secondary" size="small" @click="preview = null">
      {{ t('kb.edit.closePreview') }}
    </AppButton>
    <template v-else>
      <AppButton variant="secondary" size="small" @click="cancel">
        {{ t('kb.edit.cancel') }}
      </AppButton>
      <AppButton size="small" :loading="isSaving" @click="askVersion = true">
        {{ t('kb.edit.saveVersion') }}
      </AppButton>
    </template>
  </TrackerToolbar>

  <div class="kbedit">
    <div class="kbedit__editor">
      <template v-if="preview">
        <h1 class="kbedit__preview-title">{{ preview.title }}</h1>
        <AppRichTextEditor :model-value="preview.contentMd" readonly preset="issue" />
      </template>

      <template v-else>
        <input
          v-model="title"
          class="kbedit__title"
          :aria-label="t('kb.edit.titleLabel')"
          :placeholder="t('kb.edit.titlePlaceholder')"
        />

        <AppRichTextEditor
          v-model="contentMd"
          preset="issue"
          :placeholder="t('kb.edit.bodyPlaceholder')"
          :upload-image="uploadEditorImage"
        />
      </template>
    </div>

    <KbVersions
      :versions="store.versions"
      :title="title"
      :content-md="contentMd"
      :opened-number="preview?.number"
      @open="preview = $event"
      @restore="restore"
    />
  </div>

  <AppModal
    v-if="askVersion"
    model-value
    :title="t('kb.edit.saveVersion')"
    size="small"
    @update:model-value="askVersion = false"
  >
    <AppTextField
      v-model="comment"
      :label="t('kb.edit.commentLabel')"
      :placeholder="t('kb.edit.commentPlaceholder')"
    />

    <template #footer>
      <AppButton variant="secondary" @click="askVersion = false">
        {{ t('common.actions.cancel') }}
      </AppButton>
      <AppButton @click="saveVersion">{{ t('kb.edit.saveVersion') }}</AppButton>
    </template>
  </AppModal>
</template>

<style scoped>
.kbedit {
  display: flex;
  flex: 1;
  align-items: stretch;
  min-height: 0;
}

.kbedit__editor {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  padding: 32px 64px;
  overflow-y: auto;
}

/* Заголовок правится там же, где текст: отдельное поле «название страницы»
   над редактором в макете не нарисовано, и переключаться между ними незачем. */
.kbedit__title,
.kbedit__preview-title {
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-primary);
  font-family: var(--font-family-sans);
  font-size: 28px;
  font-weight: var(--font-weight-bold);
}

.kbedit__title:focus {
  outline: none;
}

.kbedit__status {
  color: var(--color-success-strong, var(--color-success));
  font-size: var(--font-size-caption);
  margin: 0;
}

.kbedit__status--preview {
  color: var(--color-text-muted);
}
</style>
